import React, { useState, useEffect, useCallback } from 'react';
import { Download, CheckCircle, RotateCcw } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import ResponseModal from '../components/ResponseModal';
import toast from 'react-hot-toast';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import OrchestratorProjectMismatchBanner from '../components/SmartRouter/OrchestratorProjectMismatchBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';

interface InterviewQuestion {
  id: number;
  category: string;
  priority: number;
  question_text: string;
  follow_up_questions: string[];
  gap_reference: string;
  estimated_time: number;
  notes?: string;
  answer?: string;
  created_at: string;
  sync_status?: string;
  resolution_status?: string;
  resolved_at?: string | null;
  resolution_note?: string | null;
  resolved_by?: number | null;
}

interface InterviewResponse {
  id: number;
  question_id: number;
  response_text: string;
  respondent_name: string;
  confidence_impact: number;
  response_date: string;
}

const Interviews: React.FC = () => {
  const { currentProject } = useProject();
  const orchestratorCtx = useOrchestratorContext();
  const smartRouter = useSmartRouterStepSync();
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [activeTab, setActiveTab] = useState('All Questions');
  const [selectedPriority, setSelectedPriority] = useState('All Priorities');
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [responses, setResponses] = useState<InterviewResponse[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<InterviewQuestion | null>(null);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  // 'open' shows gaps still needing work; 'resolved' is the closed-out register
  // — the "we closed everything out and didn't miss anything" view.
  const [resolutionFilter, setResolutionFilter] = useState<'open' | 'resolved'>('open');
  // Which gap's inline "resolve" note box is open, and its draft text. Replaces
  // window.prompt(), which froze the tab (it blocks the main thread and cannot
  // be driven by automated/headless browsers, so the resolve request never
  // fired and nothing persisted).
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [savingResolveId, setSavingResolveId] = useState<number | null>(null);

  // Calculate dynamic category counts based on loaded questions
  const getCategoryCounts = useCallback(() => {
    const counts: { [key: string]: number } = {
      'All Questions': questions.length,
      'Process Clarification': 0,
      'Control Validation': 0,
      'Risk Assessment': 0,
      'Process Optimization': 0
    };

    questions.forEach(question => {
      if (question.category && counts.hasOwnProperty(question.category)) {
        counts[question.category]++;
      }
    });

    return counts;
  }, [questions]);

  const categoryCountsData = getCategoryCounts();

  const categories = [
    { name: 'All Questions', count: categoryCountsData['All Questions'] },
    { name: 'Process Clarification', count: categoryCountsData['Process Clarification'], active: true },
    { name: 'Control Validation', count: categoryCountsData['Control Validation'] },
    { name: 'Risk Assessment', count: categoryCountsData['Risk Assessment'] },
    { name: 'Process Optimization', count: categoryCountsData['Process Optimization'] }
  ];

  const loadQuestions = useCallback(async (showLoading = false) => {
    if (!currentProject) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await api.get(`/api/analysis/results/${currentProject.id}`);
      const questions = response.data.interview_questions || [];

      console.log(`📋 [INTERVIEWS] Loaded ${questions.length} interview questions for project ${currentProject.id}`);

      // Group by source for debugging
      const bySource = questions.reduce((acc: any, q: InterviewQuestion) => {
        const source = q.gap_reference?.split(' - ')[0] || 'Unknown';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});

      console.log('📊 [INTERVIEWS] Questions by source:', bySource);

      // Log SOP-sourced questions in detail
      const sopQuestions = questions.filter((q: InterviewQuestion) =>
        q.gap_reference?.startsWith('SOP') || q.gap_reference?.startsWith('Enhanced SOP')
      );

      console.log(`\n🔍 [DEBUG] Total questions: ${questions.length}`);
      console.log(`🔍 [DEBUG] SOP questions found: ${sopQuestions.length}`);

      if (sopQuestions.length > 0) {
        console.log(`\n📄 [SOP QUESTIONS] ===== DISPLAYING ${sopQuestions.length} SOP QUESTIONS =====`);

        // Group SOP questions by category for count
        const sopByCategory = sopQuestions.reduce((acc: any, q: InterviewQuestion) => {
          const cat = q.category || 'Unknown';
          acc[cat] = (acc[cat] || 0) + 1;
          return acc;
        }, {});

        console.log(`📊 [SOP QUESTIONS] Category breakdown:`, sopByCategory);
        console.log(`\n`);

        sopQuestions.forEach((q: InterviewQuestion, idx: number) => {
          console.log(`  ${idx + 1}. 📝 Question: "${q.question_text}"`);
          console.log(`     📍 Addresses (gap_reference): ${q.gap_reference}`);
          console.log(`     🎯 Category: ${q.category}`);
          console.log(`     ⚡ Priority: ${q.priority}`);
          console.log(`     🕐 Created: ${q.created_at}`);
          console.log(`     🆔 ID: ${q.id}`);
          console.log(``);
        });

        console.log(`===== END OF SOP QUESTIONS =====\n`);
      } else {
        console.log(`⚠️ [SOP QUESTIONS] No SOP questions found. All ${questions.length} questions are from other sources.`);

        // Show first 3 questions to debug
        if (questions.length > 0) {
          console.log(`\n🔍 [DEBUG] First 3 questions for debugging:`);
          questions.slice(0, 3).forEach((q: InterviewQuestion, idx: number) => {
            console.log(`  ${idx + 1}. gap_reference: "${q.gap_reference}"`);
          });
        }
      }

      setQuestions(questions);
    } catch (error) {
      console.error('❌ [INTERVIEWS] Failed to load interview questions:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [currentProject]);

  const loadResponses = useCallback(async () => {
    if (!currentProject) return;

    try {
      const response = await api.get(`/api/analysis/interview-responses/${currentProject.id}`);
      setResponses(response.data || []);
    } catch (error) {
      console.error('Failed to load responses');
    }
  }, [currentProject]);

  useEffect(() => {
    if (currentProject) {
      loadQuestions(true); // Show loading on initial load
      loadResponses();
    }
  }, [currentProject, loadQuestions, loadResponses]);

  // Auto-refresh questions every 3 seconds to detect new questions (silently in background)
  // Also updates sync_status in real-time
  useEffect(() => {
    if (!currentProject) return;

    const interval = setInterval(() => {
      loadQuestions(false); // Silent refresh without loading indicator - includes sync_status
    }, 3000);

    return () => clearInterval(interval);
  }, [currentProject, loadQuestions]);

  // Faster polling (1 second) when any question is syncing for real-time status updates
  useEffect(() => {
    if (!currentProject) return;

    const hasSyncingQuestions = questions.some(q => q.sync_status === 'syncing');

    if (!hasSyncingQuestions) return;

    const interval = setInterval(() => {
      loadQuestions(false); // Fast polling for syncing questions
    }, 1000); // 1 second interval for real-time updates

    return () => clearInterval(interval);
  }, [currentProject, questions, loadQuestions]);

  const generateQuestions = async () => {
    if (!currentProject) return;

    try {
      setIsGenerating(true);
      setLoading(true);

      toast.loading('Detecting gaps from Process Map, SOP, and Process Synthesis...', { id: 'generate-questions' });

      // Use gap-sync service to detect gaps from Process Map, SOP, and Process Synthesis
      // When regenerating, delete questions without responses first
      const response = await api.post(`/api/analysis/gap-sync/${currentProject.id}`, {
        deleteExisting: questions.length > 0 // Only delete when regenerating (questions exist)
      });

      // Console log detailed gap counts by source
      console.log(`\n📊 [GAP SYNC RESULT] =============================================`);
      console.log(`📊 [GAP SYNC] Total gaps detected: ${response.data.gaps_detected}`);
      console.log(`📊 [GAP SYNC] Total questions generated: ${response.data.questions_generated}`);

      // If the response includes gap breakdown by source, log it
      if (response.data.gap_breakdown) {
        console.log(`🗺️ [PROCESS MAP GAPS] Count: ${response.data.gap_breakdown.process_map || 0}`);
        console.log(`📄 [SOP GAPS] Count: ${response.data.gap_breakdown.sop || 0}`);
        console.log(`📊 [SYNTHESIS GAPS] Count: ${response.data.gap_breakdown.synthesis || 0}`);
      }
      console.log(`📊 [GAP SYNC RESULT] =============================================\n`);

      // Load questions immediately to show new questions
      await loadQuestions(false);

      toast.success(`✅ Detected ${response.data.gaps_detected} gaps and generated ${response.data.questions_generated} interview questions!`, {
        id: 'generate-questions',
        duration: 6000
      });

      // Show notification to check Fill Gaps tab for gaps that need attention
      if (response.data.gaps_detected > 0) {
        setTimeout(() => {
          toast('💡 Questions generated for gaps! Check the Fill Gaps tab to review and address document gaps.', {
            duration: 8000,
            icon: '📋',
            style: {
              background: '#EEF2FF',
              border: '1px solid #6366F1',
              color: '#4338CA',
            },
          });
        }, 1500);
      }

    } catch (error: any) {
      console.error('Failed to generate questions:', error);
      toast.error(
        error.response?.data?.error || 'Failed to generate questions. Please try again.',
        { id: 'generate-questions' }
      );
    } finally {
      setIsGenerating(false);
      setLoading(false);
    }
  };

  // Helper function to determine gap source (matches backend logic)
  const determineGapSource = (gapReference: string | undefined | null): string => {
    if (!gapReference || typeof gapReference !== 'string') {
      return 'Unknown';
    }

    const lowerRef = gapReference.toLowerCase();

    if (lowerRef.includes('process map') || lowerRef.includes('step ') || lowerRef.includes('process step')) {
      return 'Process Map';
    }
    if (lowerRef.includes('sop') || lowerRef.includes('section ') || lowerRef.includes('procedure')) {
      return 'SOP';
    }
    if (lowerRef.includes('synthesis') || lowerRef.includes('stakeholder') || lowerRef.includes('system')) {
      return 'Process Synthesis';
    }

    return 'Unknown';
  };

  // Mark a gap resolved (or re-open it). Closure is a separate record from the
  // answer — it says the consultant got the info, updated the SOP, and closed
  // the gap out — and it never touches the SOP body.
  const toggleResolve = async (question: InterviewQuestion, resolved: boolean, note?: string) => {
    if (!currentProject) return;
    setSavingResolveId(question.id);
    try {
      const res = await api.put(`/api/analysis/interview-question/${question.id}/resolve`, {
        resolved,
        ...(note !== undefined ? { resolution_note: note } : {}),
      });
      const updated = res.data?.question || {};
      // Apply the server's persisted values so the 3s background poll (which
      // reloads all questions) can't disagree with what we just set.
      setQuestions(prev => prev.map(q => q.id === question.id
        ? {
            ...q,
            resolution_status: updated.resolution_status ?? (resolved ? 'resolved' : 'open'),
            resolved_at: updated.resolved_at ?? (resolved ? new Date().toISOString() : null),
            resolution_note: updated.resolution_note ?? (note ?? q.resolution_note ?? null),
          }
        : q));
      // Close the inline note box.
      setResolvingId(null);
      setResolveNote('');
      toast.success(resolved ? 'Gap marked resolved' : 'Gap re-opened');
    } catch (error) {
      console.error('Failed to update gap resolution:', error);
      toast.error('Failed to update gap resolution');
    } finally {
      setSavingResolveId(null);
    }
  };

  const saveResponse = async (responseData: {
    question_id: number;
    response_text: string;
    respondent_name: string;
    confidence_impact: number;
    answer?: string;
    notes?: string;
  }) => {
    if (!currentProject) return;

    try {
      await api.post(`/api/analysis/interview-response/${currentProject.id}`, responseData);
      if (responseData.response_text || responseData.notes) {
        await api.put(`/api/analysis/interview-question/${responseData.question_id}/notes`, {
          notes: responseData.notes || '',
          answer: responseData.response_text || ''
        });
      }

      await loadResponses();

      toast.success('Response saved successfully');

      // Show auto-update notifications when answer is provided
      if (responseData.answer && responseData.answer.trim().length > 0) {
        // Determine gap source from selectedQuestion
        const gapSource = determineGapSource(selectedQuestion?.gap_reference);
        // Extract only the first part before " - " for cleaner display
        const fullGapRef = selectedQuestion?.gap_reference || 'Unknown location';
        const gapRef = fullGapRef.includes(' - ') ? fullGapRef.split(' - ')[0] : fullGapRef;

        setTimeout(() => {
          toast.success('✅ Answer saved! Conditional auto-update triggered...', {
            duration: 4000,
            style: {
              border: '1px solid #10b981',
              padding: '16px',
            },
          });
        }, 500);

        // Show specific update notifications based on gap source
        setTimeout(() => {
          if (gapSource === 'Process Map') {
            toast.success(`🗺️ Updating: Process Synthesis + Process Map as gap is from ${gapSource} "${gapRef}"`, {
              duration: 6000,
            });
          } else if (gapSource === 'SOP' || gapSource === 'Enhanced SOP') {
            toast.success(`📄 Updating: Process Synthesis + SOP as gap is from ${gapSource} "${gapRef}"`, {
              duration: 6000,
            });
          } else if (gapSource === 'Process Synthesis') {
            toast.success(`📊 Updating: Process Synthesis only as gap is from ${gapSource}`, {
              duration: 6000,
            });
          } else {
            toast.success(`🔄 Updating: Process Synthesis + Process Map as gap source is ${gapSource} "${gapRef}"`, {
              duration: 6000,
            });
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to save response:', error);
      throw error;
    }
  };

  const getQuestionResponse = (questionId: number) => {
    return responses.find(r => r.question_id === questionId);
  };



  // const updateQuestionNotes = async (questionId: number, notes: string, answer: string) => {
  //   if (!currentProject) return;

  //   try {
  //     setUpdatingQuestions(prev => new Set(prev).add(questionId));

  //     const response = await api.put(`/api/analysis/interview-question/${questionId}/notes`, {
  //       notes,
  //       answer
  //     });

  //     // Check if migration is needed
  //     if (response.data.migration_needed) {
  //       alert('Database migration required. Please contact your administrator to run: npm run migrate');
  //       return;
  //     }

  //     // Update local state
  //     setQuestions(prev => prev.map(q => 
  //       q.id === questionId ? { ...q, notes, answer } : q
  //     ));

  //   } catch (error) {
  //     console.error('Failed to update question notes:', error);
  //     alert('Failed to save notes. Please try again.');
  //   } finally {
  //     setUpdatingQuestions(prev => {
  //       const newSet = new Set(prev);
  //       newSet.delete(questionId);
  //       return newSet;
  //     });
  //   }
  // };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return 'text-red-600 bg-red-100';
      case 2: return 'text-yellow-600 bg-yellow-100';
      case 3: return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityText = (priority: number) => {
    if (priority <= 3) return 'High';
    if (priority <= 6) return 'Medium';
    return 'Low';
  };

  const exportQuestions = async (format: 'word' | 'pdf') => {
    if (!currentProject) {
      console.error('No project selected');
      return;
    }

    try {
      setLoading(true);

      // Determine which questions to export
      let questionsToExport;
      if (selectedQuestions.length > 0) {
        // Export only selected questions
        questionsToExport = selectedQuestions;
      } else {
        // Export all currently filtered/visible questions
        questionsToExport = filteredQuestions.map(q => q.id);
      }

      const response = await api.post(`/api/export/interview-questions/${currentProject.id}`, {
        format,
        selectedQuestions: questionsToExport
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `interview-questions-${currentProject.name.replace(/[^a-zA-Z0-9]/g, '_')}-${timestamp}.${format === 'pdf' ? 'pdf' : 'docx'}`;
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error(`Failed to export questions as ${format}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine source priority
  const getSourcePriority = (question: InterviewQuestion) => {
    const gapRef = question.gap_reference || '';
    if (gapRef.startsWith('SOP') || gapRef.startsWith('Enhanced SOP')) return 1;
    if (gapRef.startsWith('Process Map')) return 2;
    if (gapRef.startsWith('Process Synthesis')) return 3;
    return 4; // Unknown sources
  };

  const isResolved = (q: InterviewQuestion) => q.resolution_status === 'resolved';
  const resolvedCount = questions.filter(isResolved).length;
  const openGapCount = questions.length - resolvedCount;

  const filteredQuestions = questions
    .filter(question => {
      // Closed-out register vs. still-open gaps.
      if (resolutionFilter === 'resolved' ? !isResolved(question) : isResolved(question)) return false;
      if (activeTab !== 'All Questions' && question.category !== activeTab) return false;
      if (selectedPriority !== 'All Priorities' && getPriorityText(question.priority) !== selectedPriority) return false;
      return true;
    })
    .sort((a, b) => {
      // Priority order: SOP > Process Map > Process Synthesis > Others
      const priorityDiff = getSourcePriority(a) - getSourcePriority(b);
      if (priorityDiff !== 0) return priorityDiff;

      // Within same source, sort by question priority (lower number = higher priority)
      return a.priority - b.priority;
    });

  const answeredCount = questions.filter((q) => q.answer && q.answer.trim().length > 0).length;
  const orchestratorBanner = orchestratorCtx.inRouter ? (
    <>
    <OrchestratorProjectMismatchBanner />
    <SmartRouterBanner
      stepLabel="Interviews — fill gaps"
      canFinish={answeredCount > 0}
      disabledReason="Answer at least one question before finishing."
      buildCompletionPayload={async () => {
        const payload = (() => {
        const answered = questions.filter((q) => q.answer && q.answer.trim().length > 0);
        const rows = answered.map((q) => [
          q.question || '',
          q.answer || '',
          q.category || '',
          getPriorityText(q.priority) || '',
        ]);
        const narrative = answered
          .map((q, i) => `### Q${i + 1}. ${q.question}\n\n**Answer:** ${q.answer}`)
          .join('\n\n');
        const verifiableText = answered
          .map((q) => `Q: ${q.question}\nA: ${q.answer}`)
          .join('\n\n');
        return {
          artifact: {
            refs: [{
              kind: 'interview_responses',
              label: `${answeredCount} of ${questions.length} questions answered`,
            }],
            output_payload: {
              total_questions: questions.length,
              answered: answeredCount,
              responses_count: responses.length,
              analysis: {
                narrative,
                findings: answered.map((q) => ({
                  title: q.question || 'Interview question',
                  detail: q.answer || '',
                  evidence: q.gap_reference || '',
                })),
                tables: rows.length > 0
                  ? [{ title: 'SME Interview Responses', columns: ['Question', 'Answer', 'Category', 'Priority'], rows }]
                  : [],
                gaps: questions
                  .filter((q) => !q.answer || !q.answer.trim())
                  .map((q) => q.question)
                  .slice(0, 50),
              },
              analysis_narrative: narrative,
              interview_responses: answered.map((q) => ({
                question: q.question,
                answer: q.answer,
                category: q.category,
                priority: q.priority,
                gap_reference: q.gap_reference,
              })),
            },
            verifiable_text: verifiableText.slice(0, 50000),
          },
          summary: `Filled gaps: answered ${answeredCount} SME interview questions.`,
        };
        })();
        if (!payload) return null;
        await smartRouter.finishStep(payload.artifact, payload.summary);
        return null;
      }}
    />
    </>
  ) : null;

  return (
    <div className="space-y-6">
      {orchestratorBanner}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">SME Interview Questions</h1>
          <p className="text-gray-600">Generate prioritized interview questions based on analysis gaps for {currentProject?.name}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-3">
        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue"
        >
          <option>All Priorities</option>
          <option>High</option>
          <option>Medium</option>
          <option>Low</option>
        </select>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="selectAll"
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedQuestions(filteredQuestions.map(q => q.id));
              } else {
                setSelectedQuestions([]);
              }
            }}
          />
          <label htmlFor="selectAll" className="text-sm">Select All ({selectedQuestions.length} selected)</label>
        </div>

        {questions.length > 0 && (
          <button
            onClick={generateQuestions}
            disabled={loading || isGenerating}
            className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGenerating && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            )}
            <span>{isGenerating ? 'Regenerating...' : 'Regenerate Questions'}</span>
          </button>
        )}

        <button
          onClick={() => exportQuestions('word')}
          disabled={loading}
          className="bg-electric-blue text-white px-3 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          <span>{loading ? 'Exporting...' : 'Export Word'}</span>
        </button>

        <button
          onClick={() => exportQuestions('pdf')}
          disabled={loading}
          className="bg-electric-blue text-white px-3 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          <span>{loading ? 'Exporting...' : 'Export PDF'}</span>
        </button>
      </div>

      {/* Open gaps vs. closed-out register */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setResolutionFilter('open')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${resolutionFilter === 'open'
              ? 'bg-electric-blue text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
        >
          Open Gaps ({openGapCount})
        </button>
        <button
          onClick={() => setResolutionFilter('resolved')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${resolutionFilter === 'resolved'
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
        >
          <CheckCircle className="w-4 h-4" />
          Closed-out ({resolvedCount})
        </button>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setActiveTab(category.name)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === category.name
                    ? 'border-electric-blue text-electric-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {category.name}
                {category.name !== 'All Questions' && (
                  <span className="ml-1 text-xs">({category.count})</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Questions Content */}
        <div className="p-6">
          {loading || isGenerating ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue mx-auto"></div>
              <p className="mt-4 text-gray-600 font-medium">{isGenerating ? 'Regenerating interview questions...' : 'Generating interview questions...'}</p>
              <p className="mt-2 text-sm text-gray-500">Detecting gaps from SOP, Process Map, and Process Synthesis...</p>
            </div>
          ) : filteredQuestions.length === 0 && resolutionFilter === 'resolved' ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No closed-out gaps yet</h3>
              <p className="text-gray-500">
                When you mark a gap resolved, it moves here — your record that everything was closed out and nothing was missed.
              </p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💬</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions Generated</h3>
              <p className="text-gray-500 mb-2">
                Generate intelligent interview questions based on document analysis gaps and template requirements
              </p>
              <p className="text-sm text-blue-600 mb-4">
                💡 Questions will auto-refresh every 3 seconds in backend - no need to manually refresh the page
              </p>
              <button
                onClick={generateQuestions}
                disabled={loading || isGenerating}
                className="bg-electric-blue text-white px-6 py-3 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
              >
                {isGenerating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                )}
                <span>{isGenerating ? 'Generating...' : 'Generate Questions'}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredQuestions.map((question) => (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(question.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedQuestions([...selectedQuestions, question.id]);
                        } else {
                          setSelectedQuestions(selectedQuestions.filter(id => id !== question.id));
                        }
                      }}
                      className="mt-1"
                    />

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(question.priority)}`}>
                            {getPriorityText(question.priority)} Priority
                          </span>
                          <span className="text-xs text-gray-500">
                            {question.category}
                          </span>
                        </div>

                        {/* Synced Badge - Show when sync_status is 'synced' */}
                        {question.sync_status === 'synced' && (
                          <div className="bg-green-100 border border-green-300 rounded px-3 py-1 flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs font-semibold text-green-700">Synced</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {(() => {
                          const questionResponse = getQuestionResponse(question.id);
                          return questionResponse ? (
                            <>
                                <div className="bg-green-50 border border-green-200 rounded p-2">
                                  <p className="text-xs text-green-800 font-medium">Response Captured</p>
                                  <p className="text-xs text-green-600">
                                    {questionResponse.respondent_name && `By: ${questionResponse.respondent_name} • `}
                                    Impact: {questionResponse.confidence_impact}/5
                                  </p>
                                </div>
                                {question.answer && question.answer.trim().length > 0 && (
                                  <button
                                    onClick={async () => {
                                      if (!currentProject) return;
                                      try {
                                        const gapSource = determineGapSource(question.gap_reference);
                                        // Extract only the first part before " - " for cleaner display
                                        const fullGapRef = question.gap_reference || 'Unknown location';
                                        const gapRef = fullGapRef.includes(' - ') ? fullGapRef.split(' - ')[0] : fullGapRef;

                                        toast.loading('Triggering auto-update...', { id: `sync-${question.id}` });
                                        await api.post(`/api/analysis/trigger-auto-update/${currentProject.id}`, {
                                          question_id: question.id,
                                          answer: question.answer
                                        });

                                        // Reload questions to get updated sync_status
                                        await loadQuestions(false);

                                        // Show specific update message based on gap source
                                        if (gapSource === 'Process Map') {
                                          toast.success(`✅ Re-sync triggered! Updating: Process Synthesis + Process Map as gap is from ${gapSource} "${gapRef}"`, {
                                            id: `sync-${question.id}`,
                                            duration: 6000
                                          });
                                        } else if (gapSource === 'SOP' || gapSource === 'Enhanced SOP') {
                                          toast.success(`✅ Re-sync triggered! Updating: Process Synthesis + SOP as gap is from ${gapSource} "${gapRef}"`, {
                                            id: `sync-${question.id}`,
                                            duration: 6000
                                          });
                                        } else if (gapSource === 'Process Synthesis') {
                                          toast.success(`✅ Re-sync triggered! Updating: Process Synthesis only as gap is from ${gapSource}`, {
                                            id: `sync-${question.id}`,
                                            duration: 6000
                                          });
                                        } else {
                                          toast.success(`✅ Re-sync triggered! Updating: Process Synthesis + Process Map as gap source is ${gapSource} "${gapRef}"`, {
                                            id: `sync-${question.id}`,
                                            duration: 6000
                                          });
                                        }
                                      } catch (error) {
                                        toast.error('Failed to trigger auto-update', { id: `sync-${question.id}` });
                                      }
                                    }}
                                    disabled={question.sync_status === 'syncing'}
                                    className={`${
                                      question.sync_status === 'syncing'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-purple-600 hover:bg-purple-700'
                                    } text-white px-3 py-1 rounded text-xs transition-colors flex items-center gap-1`}
                                    title="Manually trigger conditional auto-update based on gap source"
                                  >
                                    {question.sync_status === 'syncing' ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                        Syncing...
                                      </>
                                    ) : (
                                      <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Re-sync
                                      </>
                                    )}
                                  </button>
                                )}
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedQuestion(question);
                                  setIsResponseModalOpen(true);
                                }}
                                className="bg-electric-blue text-white px-3 py-1 rounded text-xs hover:bg-darker-blue transition-colors"
                              >
                                Capture Response
                              </button>
                            );
                          })()}
                        </div>

                        <p className="text-gray-900 mb-2">{question.question_text}</p>

                        {question.follow_up_questions && question.follow_up_questions.length > 0 && (
                          <div className="text-sm text-gray-600">
                            <p className="font-medium">Follow-up questions:</p>
                            <ul className="list-disc list-inside ml-2">
                              {question.follow_up_questions.map((followUp, index) => (
                                <li key={index}>{followUp}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {question.gap_reference && (
                          <p className="text-xs text-gray-500 mt-2">
                            Addresses: {question.gap_reference}
                          </p>
                        )}

                        {/* Gap closure — separate from answering; records that the
                            SOP was updated and the gap is closed out. */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {isResolved(question) ? (
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-semibold text-green-700">
                                    Resolved{question.resolved_at ? ` on ${new Date(question.resolved_at).toLocaleDateString()}` : ''}
                                  </p>
                                  {question.resolution_note && (
                                    <p className="text-xs text-gray-600 mt-0.5">{question.resolution_note}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => toggleResolve(question, false)}
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 flex-shrink-0"
                                title="Re-open this gap"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Re-open
                              </button>
                            </div>
                          ) : resolvingId === question.id ? (
                            <div className="space-y-2">
                              <textarea
                                value={resolveNote}
                                onChange={(e) => setResolveNote(e.target.value)}
                                placeholder="Optional: note how this gap was closed (info obtained, SOP updated, etc.)"
                                rows={2}
                                autoFocus
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-400"
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleResolve(question, true, resolveNote)}
                                  disabled={savingResolveId === question.id}
                                  className="text-xs font-medium text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                >
                                  {savingResolveId === question.id ? (
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-3.5 h-3.5" />
                                  )}
                                  Confirm Resolved
                                </button>
                                <button
                                  onClick={() => { setResolvingId(null); setResolveNote(''); }}
                                  disabled={savingResolveId === question.id}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setResolvingId(question.id); setResolveNote(''); }}
                              className="text-xs font-medium text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Mark Resolved
                            </button>
                          )}
                        </div>

                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ResponseModal
        isOpen={isResponseModalOpen}
        onClose={() => {
          setIsResponseModalOpen(false);
          setSelectedQuestion(null);
        }}
        question={selectedQuestion!}
        onSave={saveResponse}
      />
    </div>
  );
};

export default Interviews;